@if (loading) {
  <div class="flex items-center justify-center py-10">
    <div class="loading loading-spinner loading-lg"></div>
  </div>
} @else if (loadError) {
  <div class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
    {{ loadError }}
  </div>
} @else {
<div class="my-10">
  @if (event) {
    <h3 class="text-lg font-semibold">{{ event.name }}</h3>
    <p class="text-sm text-gray-600">Notes</p>
  }
</div>

@if ((event.notes || []).length > 0) {
  <div class="overflow-hidden">

    <div class="space-y-4">
      @for (note of event.notes; track note.id) {
        <div class="p-4 border border-gray-200 rounded-lg bg-white dark:border-gray-800 dark:bg-gray-900">

          <!-- Display the note -->
          <div class="mb-2 text-theme-sm text-gray-700 dark:text-gray-400">
            {{ note.created_at | date: 'dd/MM/yyyy HH:mm' }} by {{ note.created_by_username }}
          </div>
          @if (editNoteId === note.id) {
            <!-- Edit form for the current note -->
            <div class="-mx-2.5 mt-5 flex flex-wrap gap-y-5">
              <div class="w-full px-2.5">
                @if (editor) {
                  <div class="NgxEditor__Wrapper min-h-40 h-auto border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                    <ngx-editor-menu [editor]="editor" [toolbar]="toolbar"></ngx-editor-menu>
                    <ngx-editor
                      [formControl]="form.controls.note"
                      [editor]="editor"
                      [placeholder]="'Type here...'"
                    ></ngx-editor>
                  </div>
                }
              </div>

              <!-- File attachment section -->
              <div class="w-full px-2.5">
                @if (note.has_attachment && !form.value.remove_attachment) {
                  <div class="flex items-center gap-2 p-2 border rounded-md bg-gray-50 dark:bg-gray-800">
                    <!-- Show image preview if it's an image -->
                    @if (note.attachment?.is_image) {
                      <img [src]="note.attachment?.thumb_sm || note.attachment?.url" alt="Preview" class="h-12 w-12 object-cover rounded mr-1" />
                    }
                    <span class="flex items-center gap-1">
                        <lucide-icon [size]="16" [name]="getFileIcon(note)"></lucide-icon>
                        <span class="text-sm truncate">{{ note.attachment?.original_name }}</span>
                      </span>
                    <button type="button" (click)="removeAttachment()"
                            class="ml-auto text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300">
                      <lucide-icon [size]="16" [name]="XIcon"></lucide-icon>
                    </button>
                  </div>
                } @else if (form.value.attachment) {
                  <div class="flex items-center gap-2 p-2 border rounded-md bg-gray-50 dark:bg-gray-800">
                    <!-- Preview for newly selected image files -->
                    @if (isImageFile(form.value.attachment)) {
                      <img [src]="previewUrl" alt="Preview" class="h-12 w-12 object-cover rounded mr-1" />
                    }
                    <span class="flex items-center gap-1">
                        <lucide-icon [size]="16" [name]="FileTextIcon"></lucide-icon>
                        <span class="text-sm truncate">{{ getFileName(form.value.attachment) }}</span>
                      </span>
                    <button type="button" (click)="removeAttachment()"
                            class="ml-auto text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300">
                      <lucide-icon [size]="16" [name]="XIcon"></lucide-icon>
                    </button>
                  </div>
                } @else if (form.value.remove_attachment) {
                  <div class="text-sm text-red-500 dark:text-red-400 p-2">
                    Attachment will be removed upon saving
                    <button type="button" (click)="cancelAttachmentRemoval()" class="ml-2 underline">
                      Cancel removal
                    </button>
                  </div>
                } @else {
                  <div class="mt-2">
                    <label for="file-upload-edit" class="flex items-center gap-2 cursor-pointer p-2 border border-dashed rounded-md hover:bg-gray-50 dark:hover:bg-gray-800">
                      <lucide-icon [size]="16" [name]="PaperclipIcon"></lucide-icon>
                      <span class="text-sm">Attach file</span>
                    </label>
                    <input id="file-upload-edit" type="file" class="hidden" (change)="onFileSelected($event)" />
                  </div>
                }
              </div>

              <div class="w-full mt-3 flex justify-end gap-3">
                <app-button [label]="'Cancel'" [disabled]="processing" [style]="'secondary'" (click)="cancelEdit()"></app-button>
                <app-button [label]="'Update Note'" [disabled]="processing || !form.valid || form.pristine" (click)="save()"></app-button>
              </div>
            </div>
          } @else {
            <div class="mb-4 text-theme-sm text-gray-700 dark:text-gray-400 prose prose-sm dark:prose-invert max-w-none [&_a]:text-primary-500 [&_a]:underline [&_a]:hover:text-primary-700 dark:[&_a]:text-primary-400 dark:[&_a]:hover:text-primary-300" [innerHTML]="note.note"></div>

            <!-- Display attachment if exists -->
            @if (note.has_attachment) {
              <div class="mb-4 flex items-center gap-2 p-2 border rounded-md bg-gray-50 dark:bg-gray-800 max-w-fit">
                <!-- Show image preview if it's an image -->
                @if (note.attachment?.is_image) {
                  <img 
                    [src]="note.attachment?.thumb_sm || note.attachment?.url" 
                    alt="Preview" 
                    class="h-12 w-12 object-cover rounded mr-1 cursor-pointer hover:opacity-80 transition-opacity" 
                    (click)="viewFullImage(note.attachment?.url || '')"
                    (error)="onImageError($event, note)"
                  />
                } @else {
                  <lucide-icon [size]="16" [name]="getFileIcon(note)"></lucide-icon>
                }
                <span class="text-sm truncate max-w-[200px]" title="{{note.attachment?.original_name}}">{{ note.attachment?.original_name }}</span>
                <button (click)="downloadAttachment(note.id)"
                        class="ml-2 text-primary-500 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                        title="Download attachment">
                  <lucide-icon [size]="16" [name]="DownloadIcon"></lucide-icon>
                </button>
              </div>
            }

            <div class="flex justify-end gap-2">
              <button (click)="editNote(note.id)" class="text-gray-500 hover:text-error-500 dark:text-gray-400 dark:hover:text-error-500">
                <lucide-icon [size]="20" [name]="EditIcon"></lucide-icon>
              </button>
              <button (click)="deleteNote(note.id)" class="text-gray-500 hover:text-error-500 dark:text-gray-400 dark:hover:text-error-500">
                <lucide-icon [size]="20" [name]="TrashIcon"></lucide-icon>
              </button>
            </div>
          }
        </div>
      }
    </div>
  </div>
}

@if (form && !editNoteId) {
  <div class="-mx-2.5 mt-5 flex flex-wrap gap-y-1">
    <div class="w-full px-2.5">
      <p class="my-2">Create a new note for the event</p>
      @if (editor) {
        <div class="NgxEditor__Wrapper min-h-40 h-auto border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
          <ngx-editor-menu [editor]="editor" [toolbar]="toolbar"></ngx-editor-menu>
          <ngx-editor
            [formControl]="form.controls.note"
            [editor]="editor"
            [placeholder]="'Type here...'"
          ></ngx-editor>
        </div>
      }
    </div>

    <!-- File attachment for new note -->
    <div class="w-full px-2.5">
      @if (form.value.attachment) {
        <div class="flex items-center gap-2 p-2 border rounded-md bg-gray-50 dark:bg-gray-800">
          <!-- Preview for newly selected image files -->
          @if (isImageFile(form.value.attachment)) {
            <img [src]="previewUrl" alt="Preview" class="h-12 w-12 object-cover rounded mr-1" />
          }
          <span class="flex items-center gap-1">
            <lucide-icon [size]="16" [name]="FileTextIcon"></lucide-icon>
            <span class="text-sm truncate">{{ getFileName(form.value.attachment) }}</span>
          </span>
          <button type="button" (click)="removeAttachment()"
                  class="ml-auto text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300">
            <lucide-icon [size]="16" [name]="XIcon"></lucide-icon>
          </button>
        </div>
      } @else {
        <div class="mt-2">
          <label for="file-upload-new" class="flex items-center gap-2 cursor-pointer p-2 border border-dashed rounded-md hover:bg-gray-50 dark:hover:bg-gray-800">
            <lucide-icon [size]="16" [name]="PaperclipIcon"></lucide-icon>
            <span class="text-sm">Attach file</span>
          </label>
          <input id="file-upload-new" type="file" class="hidden" (change)="onFileSelected($event)" />
        </div>
      }
    </div>

    <div class="w-full mt-3 flex justify-end gap-3">
      <app-button [label]="'Cancel'" [disabled]="processing" [style]="'secondary'" (click)="cancel()"></app-button>
      <app-button [label]="editNoteId ? 'Update Note' : 'Create Note'" [disabled]="processing || !form.valid || form.pristine" (click)="save()"></app-button>
    </div>
  </div>
}

<app-modal
  [title]="'Delete Note'"
  [content]="'Are you sure you want to delete this note?'"
  [show]="isDeleteModalOpen"
  [closeButtonLabel]="'Cancel'"
  [confirmButtonLabel]="'Yes, Proceed'"
  (onClose)="isDeleteModalOpen = false"
  (onConfirm)="confirmDelete()"
></app-modal>
}
